import styles from './ScoopScreenLayout.module.scss'
import { ScoopText } from '../ScoopText/ScoopText'
import { ScoopSearchBar } from '../ScoopSearchBar/ScoopSearchBar'

/**
 * ScoopScreenLayout - A reusable layout component for screen pages
 * Based on the DatasetIndex layout pattern
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - The main title of the screen
 * @param {string} [props.description] - Optional description text below the title
 * @param {string|React.ReactNode} [props.icon] - Icon to display (image URL or React element)
 * @param {boolean} [props.withSearchBar=false] - Whether to show the integrated search bar
 * @param {string} [props.searchPlaceholder='Search...'] - Placeholder text for the search bar
 * @param {string} [props.searchValue=''] - Controlled value for the search bar
 * @param {function} [props.onSearchChange] - Callback when search value changes (receives value string)
 * @param {string} [props.searchWidth='400px'] - Width of the search bar
 * @param {React.ReactNode} [props.headerActions] - Action buttons/components to display on the right side of the header
 * @param {React.ReactNode} props.children - Main content to render below the header
 * @param {string} [props.className] - Additional CSS classes for the container
 * @param {string} [props.contentClassName] - Additional CSS classes for the content area
 */
export const ScoopScreenLayout = ({
    title,
    description,
    icon,
    withSearchBar = false,
    searchPlaceholder = 'Search...',
    searchValue = '',
    onSearchChange,
    searchWidth = '400px',
    headerActions,
    children,
    className = '',
    contentClassName = '',
}) => {
    return (
        <div className={`${styles.screenContainer} ${className}`}>
            <div className={styles.screenTopbar}>
                <div className={styles.topBarRow}>
                    <div className={styles.headerContentRow}>
                        {icon && (
                            <div className={styles.iconContainer}>
                                {typeof icon === 'string' ? (
                                    <img src={icon} alt={title} className={styles.headerIcon} />
                                ) : (
                                    icon
                                )}
                            </div>
                        )}
                        <div className={styles.titleColumn}>
                            <ScoopText className={styles.titleContainer}>{title}</ScoopText>
                            {description && (
                                <ScoopText className={styles.descriptionText}>
                                    {description}
                                </ScoopText>
                            )}
                        </div>
                    </div>
                </div>
                {(withSearchBar || headerActions) && (
                    <div className={styles.actionsRow}>
                        {withSearchBar && (
                            <ScoopSearchBar
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onChange={(e) => onSearchChange?.(e.target.value)}
                                width={searchWidth}
                            />
                        )}
                        {headerActions}
                    </div>
                )}
            </div>
            <div className={`${styles.contentContainer} ${contentClassName}`}>{children}</div>
        </div>
    )
}

export default ScoopScreenLayout
